import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { PeriodSelector } from '../components/PeriodSelector';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Upload, 
  TrendingUp,
  Coffee,
  UtensilsCrossed,
  Award,
  BarChart3,
  PieChart,
  Package,
  Calendar
} from 'lucide-react';

const ProductMixPage = () => {
  const { currentLocation, api } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [periodStart, setPeriodStart] = useState(startOfMonth(new Date()));
  const [periodEnd, setPeriodEnd] = useState(endOfMonth(new Date()));
  const [periodPreset, setPeriodPreset] = useState('this_month');

  useEffect(() => {
    if (currentLocation) {
      fetchData();
    }
  }, [currentLocation, periodStart, periodEnd]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const dateParams = {
        location_id: currentLocation.id,
        period_start: format(periodStart, 'yyyy-MM-dd'),
        period_end: format(periodEnd, 'yyyy-MM-dd'),
        limit: 100,
      };
      const [summaryRes, itemsRes] = await Promise.all([
        api.get('/product-mix/summary', { params: { location_id: currentLocation.id, period_start: format(periodStart, 'yyyy-MM-dd'), period_end: format(periodEnd, 'yyyy-MM-dd') } }),
        api.get('/product-mix/items', { params: dateParams })
      ]);
      setSummary(summaryRes.data);
      setAllItems(itemsRes.data);
    } catch (err) {
      console.error('Error fetching product mix:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (val) => `£${(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatNumber = (val) => (val || 0).toLocaleString('en-GB');

  // Calculate max values for progress bars
  const maxMenuRevenue = summary?.menu_breakdown?.length > 0 
    ? Math.max(...summary.menu_breakdown.map(m => m.gross_sales)) 
    : 1;
  const maxGroupRevenue = summary?.menu_group_breakdown?.length > 0 
    ? Math.max(...summary.menu_group_breakdown.map(g => g.gross_sales)) 
    : 1;

  if (loading) {
    return (
      <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  const hasData = summary && summary.item_count > 0;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto space-y-6" data-testid="product-mix-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Product Mix</h1>
          <p className="text-muted-foreground">Menu item performance and sales breakdown</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <PeriodSelector
            startDate={periodStart}
            endDate={periodEnd}
            preset={periodPreset}
            onPresetChange={setPeriodPreset}
            onChange={(s, e) => { setPeriodStart(s); setPeriodEnd(e); }}
          />
          <Button
            variant="outline"
            onClick={() => navigate('/imports?tab=product_mix')}
            data-testid="open-imports-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            Import product mix
          </Button>
        </div>
      </div>

      {!hasData ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold text-lg mb-2">No product mix data yet</h3>
            <p className="text-muted-foreground mb-4">
              Product Mix reports are imported from the Imports hub.
            </p>
            <Button onClick={() => navigate('/imports?tab=product_mix')} data-testid="empty-open-imports-btn">
              <Upload className="w-4 h-4 mr-2" />
              Go to Imports
            </Button>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Period Info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4" />
            Data from {new Date(summary.period_start).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} to {new Date(summary.period_end).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="card-hover">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Menu Items
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{summary.item_count}</div>
                <p className="text-sm text-muted-foreground">unique items sold</p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Items Sold
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatNumber(summary.total_qty_sold)}</div>
                <p className="text-sm text-muted-foreground">total quantity</p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Gross Sales
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-profit">{formatCurrency(summary.total_gross_sales)}</div>
                <p className="text-sm text-muted-foreground">total revenue</p>
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Avg Item Price
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatCurrency(summary.total_gross_sales / summary.total_qty_sold)}
                </div>
                <p className="text-sm text-muted-foreground">per item</p>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="top-sellers" className="space-y-4">
            <TabsList>
              <TabsTrigger value="top-sellers" data-testid="tab-top-sellers">
                <Award className="w-4 h-4 mr-2" />
                Top Sellers
              </TabsTrigger>
              <TabsTrigger value="categories" data-testid="tab-categories">
                <PieChart className="w-4 h-4 mr-2" />
                Categories
              </TabsTrigger>
              <TabsTrigger value="all-items" data-testid="tab-all-items">
                <Package className="w-4 h-4 mr-2" />
                All Items
              </TabsTrigger>
            </TabsList>

            {/* Top Sellers Tab */}
            <TabsContent value="top-sellers" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top 10 by Revenue */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-profit" />
                      Top 10 by Revenue
                    </CardTitle>
                    <CardDescription>Highest grossing items</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {summary.top_items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{item.item_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {formatNumber(item.qty_sold)} sold @ {formatCurrency(item.avg_price)} avg
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-profit">{formatCurrency(item.gross_sales)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top 10 by Quantity */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      Top 10 by Volume
                    </CardTitle>
                    <CardDescription>Most frequently ordered items</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {[...allItems]
                        .sort((a, b) => b.qty_sold - a.qty_sold)
                        .slice(0, 10)
                        .map((item, idx) => (
                          <div key={idx} className="flex items-center gap-4">
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-sm font-bold">
                              {idx + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{item.item_name}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <Progress 
                                  value={(item.qty_sold / allItems[0]?.qty_sold) * 100} 
                                  className="h-2 flex-1"
                                />
                                <span className="text-sm font-medium w-16 text-right">
                                  {formatNumber(item.qty_sold)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Categories Tab */}
            <TabsContent value="categories" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Menu Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <UtensilsCrossed className="w-5 h-5" />
                      Menu Categories
                    </CardTitle>
                    <CardDescription>Sales by main menu type</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {summary.menu_breakdown.map((menu, idx) => {
                        const percentage = (menu.gross_sales / summary.total_gross_sales) * 100;
                        return (
                          <div key={idx} className="space-y-2">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                {menu.menu_name === 'Brunch' && <UtensilsCrossed className="w-4 h-4 text-amber-500" />}
                                {menu.menu_name === 'Sips & Treats' && <Coffee className="w-4 h-4 text-amber-700" />}
                                {menu.menu_name === 'Signature Burgers' && <UtensilsCrossed className="w-4 h-4 text-red-500" />}
                                {!['Brunch', 'Sips & Treats', 'Signature Burgers'].includes(menu.menu_name) && 
                                  <Package className="w-4 h-4 text-muted-foreground" />}
                                <span className="font-medium">{menu.menu_name}</span>
                              </div>
                              <div className="text-right">
                                <span className="font-semibold text-profit">{formatCurrency(menu.gross_sales)}</span>
                                <span className="text-sm text-muted-foreground ml-2">({percentage.toFixed(1)}%)</span>
                              </div>
                            </div>
                            <Progress value={(menu.gross_sales / maxMenuRevenue) * 100} className="h-3" />
                            <p className="text-sm text-muted-foreground">
                              {formatNumber(menu.qty_sold)} items sold
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Menu Groups Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-5 h-5" />
                      Menu Groups
                    </CardTitle>
                    <CardDescription>Sales by subcategory</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 max-h-[500px] overflow-y-auto pr-2">
                      {summary.menu_group_breakdown.slice(0, 15).map((group, idx) => {
                        const percentage = (group.gross_sales / summary.total_gross_sales) * 100;
                        return (
                          <div key={idx} className="flex items-center gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium truncate">{group.menu_group}</span>
                                <span className="text-sm text-muted-foreground">{percentage.toFixed(1)}%</span>
                              </div>
                              <Progress value={(group.gross_sales / maxGroupRevenue) * 100} className="h-2" />
                            </div>
                            <div className="w-20 text-right">
                              <span className="text-sm font-medium">{formatCurrency(group.gross_sales)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue Distribution Visual */}
              <Card>
                <CardHeader>
                  <CardTitle>Revenue Distribution</CardTitle>
                  <CardDescription>Visual breakdown of sales by menu category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex h-12 rounded-lg overflow-hidden">
                    {summary.menu_breakdown.map((menu, idx) => {
                      const percentage = (menu.gross_sales / summary.total_gross_sales) * 100;
                      const colors = [
                        'bg-amber-500',
                        'bg-amber-700',
                        'bg-red-500',
                        'bg-slate-400'
                      ];
                      return (
                        <div
                          key={idx}
                          className={`${colors[idx % colors.length]} flex items-center justify-center text-white text-sm font-medium transition-all hover:opacity-80`}
                          style={{ width: `${percentage}%` }}
                          title={`${menu.menu_name}: ${formatCurrency(menu.gross_sales)} (${percentage.toFixed(1)}%)`}
                        >
                          {percentage > 10 && menu.menu_name}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex flex-wrap gap-4 mt-4">
                    {summary.menu_breakdown.map((menu, idx) => {
                      const colors = [
                        'bg-amber-500',
                        'bg-amber-700',
                        'bg-red-500',
                        'bg-slate-400'
                      ];
                      return (
                        <div key={idx} className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded ${colors[idx % colors.length]}`} />
                          <span className="text-sm">{menu.menu_name}</span>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* All Items Tab */}
            <TabsContent value="all-items">
              <Card>
                <CardHeader>
                  <CardTitle>All Menu Items</CardTitle>
                  <CardDescription>{allItems.length} items with sales data</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Item</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-center">Qty Sold</TableHead>
                          <TableHead className="text-right">Avg Price</TableHead>
                          <TableHead className="text-right">Gross Sales</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allItems.map((item, idx) => (
                          <TableRow key={idx} className="table-row-hover">
                            <TableCell className="font-medium max-w-[200px] truncate">
                              {item.item_name}
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">
                                {item.sales_category || 'Uncategorized'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-center">
                              {formatNumber(item.qty_sold)}
                            </TableCell>
                            <TableCell className="text-right mono-num">
                              {formatCurrency(item.avg_price)}
                            </TableCell>
                            <TableCell className="text-right mono-num font-medium text-profit">
                              {formatCurrency(item.gross_sales)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
};

export default ProductMixPage;
